import React, { useEffect, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Grid,
    Chip,
    CircularProgress,
    Divider,
    Stack,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableContainer,
    Paper,
    InputAdornment
} from "@mui/material";

import LinkIcon from "@mui/icons-material/Link";
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import KeyIcon from "@mui/icons-material/Key";
import ApartmentIcon from "@mui/icons-material/Apartment";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";

import Swal from "sweetalert2";
import { db, auth } from "../firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

import Menu from "../panel/Menu";

function Cmconnection() {
    const [propertyId, setPropertyId] = useState("");
    const [authCode, setAuthCode] = useState("");
    const [hotelCode, setHotelCode] = useState("");
    const [loading, setLoading] = useState(false);
    const [connected, setConnected] = useState(false);
    const [rooms, setRooms] = useState([]);

    const urls = {
        baseUrl: "https://pmsapi.channelmanager.com.au/api/cmpms",
        roomUrl: "https://pmsapi.channelmanager.com.au/api/cmpms/roominfo",
        inventoryUrl: "https://pmsapi.channelmanager.com.au/api/cmpms/UpdateInventory",
        rateUrl: "https://pmsapi.channelmanager.com.au/api/cmpms/rateupdat",
        restrictionUrl: "https://pmsapi.channelmanager.com.au/api/cmpms/UpdateRestriction"
    };

    /* FETCH PROPERTY ID */
    useEffect(() => {
        const fetchProperty = async () => {
            try {
                const uid = auth.currentUser?.uid;
                if (!uid) return;

                const docRef = doc(db, "authroles", uid);
                const snap = await getDoc(docRef);

                if (snap.exists()) {
                    setPropertyId(snap.data().propertyId);
                }
            } catch (error) {
                console.error("Error fetching property ID:", error);
            }
        };
        fetchProperty();
    }, []);

    /* FETCH CHANNEL MANAGER SETTINGS */
    useEffect(() => {
        const fetchChannelManager = async () => {
            if (!propertyId) return;
            try {
                const ref = doc(db, "channel_manager", propertyId);
                const snap = await getDoc(ref);

                if (snap.exists()) {
                    const data = snap.data();
                    setAuthCode(data.authCode || "");
                    setHotelCode(data.hotelCode || "");
                    setConnected(data.status === "connected");
                }
            } catch (error) {
                console.error("Error fetching channel manager settings:", error);
            }
        };
        fetchChannelManager();
    }, [propertyId]);

    /* SAVE SETTINGS */
    const saveConfig = async () => {
        if (!authCode || !hotelCode) {
            Swal.fire({ icon: "warning", title: "Missing Information", text: "Please enter both Hotel Code and AuthCode." });
            return;
        }
        if (!propertyId) {
            Swal.fire({ icon: "error", title: "Error", text: "Property ID not loaded yet. Please wait." });
            return;
        }

        try {
            setLoading(true);
            const ref = doc(db, "channel_manager", propertyId);

            await setDoc(ref, {
                propertyId,
                authCode,
                hotelCode,
                baseUrl: urls.baseUrl,
                roomUrl: urls.roomUrl,
                inventoryUrl: urls.inventoryUrl,
                rateUrl: urls.rateUrl,
                restrictionUrl: urls.restrictionUrl,
                status: "connected",
                updatedAt: serverTimestamp()
            }, { merge: true });

            setConnected(true);
            Swal.fire({ icon: "success", title: "Saved!", text: "Configuration connected and updated successfully." });
        } catch (error) {
            Swal.fire({ icon: "error", title: "Error Saving", text: error.message });
        } finally {
            setLoading(false);
        }
    };

    /* TEST CONNECTION + FETCH ROOM INFO */
    const testConnection = async () => {
        if (!authCode || !hotelCode) {
            Swal.fire({ icon: "warning", title: "Required Fields", text: "Please enter both AuthCode and Hotel Code to test." });
            return;
        }

        try {
            setLoading(true);
            const res = await fetch("http://localhost:5000/api/test-roominfo", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    AuthCode: authCode,
                    hotelCode: hotelCode,
                    PropertyId: propertyId
                })
            });

            const data = await res.json();

            if (data?.rooms) {
                setRooms(data.rooms);
                setConnected(true);
                Swal.fire({ icon: "success", title: "Connection Success", text: "Room details successfully pulled from Channel Manager." });
            } else {
                Swal.fire({ icon: "info", title: "No Rooms Found", text: "Connected successfully, but returned an empty room index." });
            }
        } catch (error) {
            console.error(error);
            Swal.fire({ icon: "error", title: "Connection Failed", text: "Could not establish communication with the verification server." });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ display: "flex", minHeight: "100vh", backgroundColor: "#f8fafc" }}>
            <Menu />

            <Box  className="content-wrapper"
                component="main" 
                sx={{ 
                    flexGrow: 1, 
                    p: { xs: 2, md: 4 }, 
                    mt: { xs: 7, sm: 0 },
                    transition: "all 0.3s ease" 
                }}
            >
                {/* HEADER SECTION */}
                <Box sx={{ display: "flex", justifyContent: "between", alignItems: "center", mb: 4, flexWrap: "wrap", gap: 2 }}>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: "#1e293b", letterSpacing: "-0.5px" }}>
                            Channel Manager
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>
                            Synchronize configurations, real-time rates, and room inventorial limits.
                        </Typography>
                    </Box>
                    <Chip
                        label={connected ? "Live & Connected" : "Action Required"}
                        color={connected ? "success" : "warning"}
                        variant="filled"
                        sx={{ 
                            fontWeight: 600, 
                            px: 1, 
                            height: 32,
                            borderRadius: "8px",
                            boxShadow: connected ? "0 4px 12px rgba(76, 175, 80, 0.2)" : "0 4px 12px rgba(239, 108, 0, 0.2)"
                        }}
                    />
                </Box>

                <Grid container spacing={4}>
                    {/* CONFIGURATION PANEL */}
                    <Grid item xs={12} lg={7}>
                        <Card sx={{ borderRadius: 4, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)", border: "1px solid #e2e8f0" }}>
                            <CardContent sx={{ p: 4 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: "#334155", mb: 1 }}>
                                    Connection Parameters
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#94a3b8", mb: 3 }}>
                                    Input your credentials acquired from your Channel Manager ecosystem vendor.
                                </Typography>
                                
                                <Divider sx={{ mb: 4, borderColor: "#f1f5f9" }} />

                                <Stack spacing={3}>
                                    <TextField
                                        label="Hotel Code"
                                        variant="outlined"
                                        value={hotelCode}
                                        onChange={(e) => setHotelCode(e.target.value)}
                                        fullWidth
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <ApartmentIcon sx={{ color: "#94a3b8" }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                                    />

                                    <TextField
                                        label="API Authentication Token"
                                        variant="outlined"
                                        value={authCode}
                                        onChange={(e) => setAuthCode(e.target.value)}
                                        fullWidth
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <KeyIcon sx={{ color: "#94a3b8" }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                                    />

                                    <Box sx={{ display: "flex", gap: 2, pt: 1, flexWrap: "wrap" }}>
                                        {/* <Button
                                            variant="outlined"
                                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LinkIcon />}
                                            onClick={testConnection}
                                            disabled={loading}
                                            sx={{ borderRadius: 3, px: 3, py: 1.2, textTransform: "none", fontWeight: 600 }}
                                        >
                                            Test Link
                                        </Button> */}
                                        
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CloudDoneIcon />}
                                            onClick={saveConfig}
                                            disabled={loading}
                                            sx={{ 
                                                borderRadius: 3, 
                                                px: 4, 
                                                py: 1.2, 
                                                textTransform: "none", 
                                                fontWeight: 600,
                                                backgroundColor: "#4f46e5",
                                                "&:hover": { backgroundColor: "#4338ca" },
                                                boxShadow: "0 4px 14px 0 rgba(79, 70, 229, 0.4)"
                                            }}
                                        >
                                            Save Settings
                                        </Button>
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* INFORMATION HUB */}
                    <Grid item xs={12} lg={5}>
                        <Card sx={{ borderRadius: 4, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)", border: "1px solid #e2e8f0", height: "100%" }}>
                            <CardContent sx={{ p: 4 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: "#334155", mb: 1 }}>
                                    Endpoint Architecture
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#94a3b8", mb: 3 }}>
                                    System targeted distribution hooks routed transparently.
                                </Typography>
                                <Divider sx={{ mb: 3, borderColor: "#f1f5f9" }} />

                                <Stack spacing={2.5}>
                                    {[
                                        { label: "Base API Entry", value: urls.baseUrl },
                                        { label: "Room Mapping Route", value: urls.roomUrl },
                                        { label: "Inventory Handshake", value: urls.inventoryUrl }
                                    ].map((item, idx) => (
                                        <Box key={idx}>
                                            <Typography variant="caption" sx={{ fontWeight: 600, color: "#64748b", textTransform: "uppercase" }}>
                                                {item.label}
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontFamily: "monospace", color: "#334155", wordBreak: "break-all", mt: 0.5, bg: "#f8fafc", p: 1, borderRadius: 1.5, border: "1px solid #f1f5f9" }}>
                                                {item.value}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* ROOM MAPPING SCHEDULER TABLE */}
                {rooms.length > 0 && (
                    <Card sx={{ mt: 5, borderRadius: 4, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)", border: "1px solid #e2e8f0", overflow: "hidden" }}>
                        <Box sx={{ p: 3, display: "flex", alignItems: "center", gap: 1.5, bg: "#fff", borderBottom: "1px solid #f1f5f9" }}>
                            <MeetingRoomIcon sx={{ color: "#4f46e5" }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, color: "#1e293b" }}>
                                Active Rates & Structural Mapping
                            </Typography>
                        </Box>
                        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 0 }}>
                            <Table sx={{ minWidth: 650 }}>
                                <TableHead sx={{ backgroundColor: "#f8fafc" }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Index</TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Room Identifier</TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Room Classification</TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Rate Plan Token ID</TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Assigned Target Strategy</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rooms.flatMap((room, index) => {
                                        const ratePlans = room?.RoomRatePlans?.RoomRatePlanData || [];
                                        return ratePlans.map((plan, i) => (
                                            <TableRow 
                                                key={`${room.RoomId}-${i}`}
                                                sx={{ "&:hover": { backgroundColor: "#fcfdff" }, transition: "background-color 0.2s" }}
                                            >
                                                {i === 0 ? (
                                                    <>
                                                        <TableCell rowSpan={ratePlans.length} sx={{ fontWeight: 500 }}>{index + 1}</TableCell>
                                                        <TableCell rowSpan={ratePlans.length} sx={{ fontFamily: "monospace", color: "#4f46e5", fontWeight: 600 }}>{room.RoomId}</TableCell>
                                                        <TableCell rowSpan={ratePlans.length} sx={{ fontWeight: 600, color: "#334155" }}>{room.RoomName}</TableCell>
                                                    </>
                                                ) : null}
                                                <TableCell sx={{ fontFamily: "monospace", color: "#0284c7" }}>{plan.RatePlanId}</TableCell>
                                                <TableCell>
                                                    <Chip 
                                                        label={plan.RatePlanName} 
                                                        size="small" 
                                                        sx={{ bg: "#f0fdf4", color: "#166534", fontWeight: 500, border: "1px solid #bbf7d0" }} 
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ));
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Card>
                )}
            </Box>
        </Box>
    );
}

export default Cmconnection;